// components/ui/LessonCard.tsx
import type { Video } from '../../types/course';
import type { VideoStatus } from '../../types/progress';

interface LessonCardProps {
  video: Video;
  status: VideoStatus;
  isRecommended: boolean;
  onClick: () => void;
  index: number;
}

export default function LessonCard({ video, status, isRecommended, onClick, index }: LessonCardProps) {
  const isCompleted = status === 'completed';
  const delay = Math.min(index * 50, 250);

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '';
    const m = Math.floor(seconds / 60);
    return `${m} min`;
  };

  // Determine vibrant colors based on index to recreate the reference image look
  const getCardStyle = () => {
    if (isCompleted) {
      return 'bg-surface-50 border-surface-200 opacity-80 text-surface-400';
    }
    const styles = [
      'bg-gradient-to-br from-orange-100 to-orange-50 border-orange-200 shadow-orange-100 text-orange-900',
      'bg-gradient-to-br from-purple-100 to-purple-50 border-purple-200 shadow-purple-100 text-purple-900',
      'bg-gradient-to-br from-emerald-100 to-emerald-50 border-emerald-200 shadow-emerald-100 text-emerald-900',
      'bg-gradient-to-br from-blue-100 to-blue-50 border-blue-200 shadow-blue-100 text-blue-900'
    ];
    return styles[index % styles.length];
  };

  const getIconStyle = () => {
    if (isCompleted) return 'bg-surface-200 text-surface-500';
    const styles = [
      'bg-orange-500 text-white shadow-md shadow-orange-200',
      'bg-purple-500 text-white shadow-md shadow-purple-200',
      'bg-emerald-500 text-white shadow-md shadow-emerald-200',
      'bg-blue-500 text-white shadow-md shadow-blue-200'
    ];
    return styles[index % styles.length];
  };

  const currentCardStyle = getCardStyle();
  const currentIconStyle = getIconStyle();

  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left rounded-3xl p-5 sm:p-6 border transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl
        ${currentCardStyle}
        ${isRecommended ? 'ring-2 ring-offset-2 ring-primary scale-[1.02]' : 'shadow-md'}
      `}
      style={{ animation: `fadeUp 0.4s ease-out ${delay}ms both` }}
    >
      <div className="flex items-center gap-4">
        {/* Big Avatar / Icon */}
        <div className={`
          w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-xl flex-shrink-0 transition-transform
          ${currentIconStyle}
          ${isRecommended ? 'animate-pulse' : ''}
        `}>
          {isCompleted ? (
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-2">
          <h3 className={`text-base sm:text-lg font-bold truncate leading-tight mb-1.5 ${isCompleted ? 'text-surface-500' : ''}`}>
            {video.title}
          </h3>
          <div className="flex items-center gap-2">
            {video.duration && (
              <span className={`text-xs sm:text-sm font-medium opacity-80`}>
                {formatDuration(video.duration)}
              </span>
            )}
            <span className="opacity-50 text-xs">•</span>
            {video.difficulty && (
              <span className={`text-xs font-semibold uppercase tracking-wider opacity-80`}>
                {video.difficulty}
              </span>
            )}
            <span className="opacity-50 text-xs">•</span>
            <span className={`text-xs font-semibold opacity-80 truncate`}>
               {video.description ? video.description.substring(0, 30) + '...' : 'Lesson material'}
            </span>
          </div>
        </div>

        {/* Play Icon CTA */}
        <div className="flex-shrink-0 opacity-60 flex items-center justify-center">
            {isCompleted ? (
                <span className="text-xs font-bold uppercase tracking-widest text-surface-500">Done</span>
            ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
            )}
        </div>
      </div>
    </button>
  );
}
